import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Table, Button, Modal, Form, AutoComplete, Input, Popconfirm,
  Space, Tag, message, Tooltip, Typography,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  MailOutlined, KeyOutlined, ReloadOutlined, UserOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

const { Text } = Typography;
const BASE = process.env.REACT_APP_API_URL;

interface EltEmailRecord {
  eltEmail1: string;
  eltEmailKey: string;
  displayName?: string;
}

const ELTEmail: React.FC = () => {
  const { instance, accounts } = useMsal();

  const [records, setRecords]     = useState<EltEmailRecord[]>([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<EltEmailRecord | null>(null);
  const [saving, setSaving]       = useState(false);
  const [pageSize, setPageSize]   = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [emailOptions, setEmailOptions] = useState<{ value: string; label: string }[]>([]);
  const [emailSearching, setEmailSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [form] = Form.useForm();

  /* ── Auth token ── */
  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.idToken;
  }, [instance, accounts]);

  /* ── Email autocomplete search ── */
  const handleEmailSearch = useCallback((val: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!val || val.length < 2) { setEmailOptions([]); return; }
    setEmailSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const token = await fetchToken();
        const res = await fetch(`${BASE}/api/Users/Search?search=${encodeURIComponent(val)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { setEmailOptions([]); return; }
        const data = await res.json();
        setEmailOptions(
          (data ?? []).map((u: any) => ({
            value: u.email ?? u.Email ?? '',
            label: `${u.displayName ?? u.DisplayName ?? ''} — ${u.email ?? u.Email ?? ''}`,
          }))
        );
      } catch {
        setEmailOptions([]);
      } finally {
        setEmailSearching(false);
      }
    }, 300);
  }, [fetchToken]);

  /* ── Load all records ── */
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = await fetchToken();
      const res = await fetch(`${BASE}/api/nominations/elt/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const rows: EltEmailRecord[] = (data.responseData ?? data ?? []).map((r: any) => ({
        eltEmail1:   r.eltEmail1   ?? r.elt_email   ?? '',
        eltEmailKey: r.eltEmailKey ?? r.elt_email_key ?? '',
        displayName: r.displayName ?? r.DisplayName ?? '',
      }));
      rows.sort((a, b) => a.eltEmailKey.localeCompare(b.eltEmailKey));
      setRecords(rows);
    } catch {
      message.error('Failed to load ELT email records.');
    } finally {
      setLoading(false);
    }
  }, [fetchToken]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  /* ── Open modal ── */
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setEmailOptions([]);
    setModalOpen(true);
  };

  const openEdit = (record: EltEmailRecord) => {
    setEditing(record);
    form.setFieldsValue({ eltEmail1: record.eltEmail1, eltEmailKey: record.eltEmailKey });
    setEmailOptions([]);
    setModalOpen(true);
  };

  /* ── Save (Add or Update) ── */
  const handleSave = async () => {
    let values: { eltEmail1: string; eltEmailKey: string };
    try { values = await form.validateFields(); }
    catch { return; }

    setSaving(true);
    try {
      const token = await fetchToken();

      if (editing) {
        /* Update — send old + new values so backend can locate and update the record */
        const res = await fetch(`${BASE}/api/nominations/elt/update-by-key`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            oldEltEmail:    editing.eltEmail1,
            oldEltEmailKey: editing.eltEmailKey,
            newEltEmail:    values.eltEmail1,
            newEltEmailKey: values.eltEmailKey,
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        message.success('ELT email updated successfully.');
      } else {
        /* Add new record */
        const res = await fetch(`${BASE}/api/nominations/elt/add`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ eltEmail1: values.eltEmail1, eltEmailKey: values.eltEmailKey }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text.includes('already exists') ? 'This email + key combination already exists.' : text);
        }
        message.success('ELT email added successfully.');
      }

      setModalOpen(false);
      loadRecords();
    } catch (err: any) {
      message.error(err.message || 'Operation failed.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (record: EltEmailRecord) => {
    try {
      const token = await fetchToken();
      const params = new URLSearchParams({ email: record.eltEmail1, key: record.eltEmailKey });
      const res = await fetch(`${BASE}/api/nominations/elt/delete?${params}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      message.success('ELT email deleted successfully.');
      loadRecords();
    } catch (err: any) {
      message.error(err.message || 'Delete failed.');
    }
  };

  /* ── Table columns ── */
  const columns = [
    {
      title: '#',
      width: 60,
      render: (_: any, __: any, index: number) => (
        <Text type="secondary" style={{ fontSize: 13 }}>{index + 1}</Text>
      ),
    },
    {
      title: (
        <Space><UserOutlined style={{ color: '#8629A4' }} /> Employee Name</Space>
      ),
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a: EltEmailRecord, b: EltEmailRecord) =>
        (a.displayName ?? '').localeCompare(b.displayName ?? ''),
      render: (val: string) => (
        <Text style={{ fontSize: 13.5 }}>{val || <Text type="secondary" style={{ fontSize: 12 }}>—</Text>}</Text>
      ),
    },
    {
      title: (
        <Space><MailOutlined style={{ color: '#8629A4' }} /> ELT Email</Space>
      ),
      dataIndex: 'eltEmail1',
      key: 'eltEmail1',
      sorter: (a: EltEmailRecord, b: EltEmailRecord) => a.eltEmail1.localeCompare(b.eltEmail1),
      render: (val: string) => (
        <Text style={{ fontSize: 13.5 }}>{val}</Text>
      ),
    },
    {
      title: (
        <Space><KeyOutlined style={{ color: '#8629A4' }} /> Email Key (Slot)</Space>
      ),
      dataIndex: 'eltEmailKey',
      key: 'eltEmailKey',
      width: 160,
      sorter: (a: EltEmailRecord, b: EltEmailRecord) => a.eltEmailKey.localeCompare(b.eltEmailKey),
      render: (val: string) => (
        <Tag color="purple" style={{ fontWeight: 600 }}>{val}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: EltEmailRecord) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              style={{ color: '#8629A4' }}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete ELT Email"
            description={`Remove "${record.eltEmail1}" (Key: ${record.eltEmailKey})?`}
            onConfirm={() => handleDelete(record)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* ── Toolbar ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {records.length} ELT member{records.length !== 1 ? 's' : ''} configured
        </Text>
        <Space>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={loadRecords} loading={loading} />
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{ background: '#8629A4', borderColor: '#8629A4' }}
          >
            Add ELT Member
          </Button>
        </Space>
      </div>

      {/* ── Table ── */}
      <Table
        rowKey={r => `${r.eltEmail1}-${r.eltEmailKey}`}
        columns={columns}
        dataSource={records}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: t => `Total ${t} records`,
          onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
        }}
        bordered
        size="middle"
        style={{ borderRadius: 8, overflow: 'hidden' }}
        rowClassName={(_, index) => index % 2 === 0 ? '' : 'ant-table-row-alt'}
      />

      {/* ── Add / Edit Modal ── */}
      <Modal
        title={
          <Space>
            {editing ? <EditOutlined style={{ color: '#8629A4' }} /> : <PlusOutlined style={{ color: '#8629A4' }} />}
            <span>{editing ? 'Edit ELT Member' : 'Add ELT Member'}</span>
          </Space>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Update' : 'Add'}
        confirmLoading={saving}
        okButtonProps={{ style: { background: '#8629A4', borderColor: '#8629A4' } }}
        destroyOnClose
        width={480}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="eltEmail1"
            label="ELT Email Address"
            rules={[
              { required: true, message: 'Email is required.' },
              { type: 'email', message: 'Enter a valid email address.' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const isDuplicate = records.some(
                    r => r.eltEmail1.toLowerCase() === value.toLowerCase() &&
                         r.eltEmailKey.toLowerCase() !== editing?.eltEmailKey.toLowerCase()
                  );
                  return isDuplicate
                    ? Promise.reject('This email address already exists.')
                    : Promise.resolve();
                },
              },
            ]}
          >
            <AutoComplete
              options={emailOptions}
              onSearch={handleEmailSearch}
              onSelect={(val: string) => form.setFieldValue('eltEmail1', val)}
              filterOption={false}
              notFoundContent={emailSearching ? 'Searching…' : null}
              allowClear
            >
              <Input
                prefix={<MailOutlined style={{ color: '#bbb' }} />}
                placeholder="Type name or email to search…"
              />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            name="eltEmailKey"
            label="Email Key"
            rules={[
              { required: true, message: 'Email key is required.' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const isDuplicate = records.some(
                    r => r.eltEmailKey.toLowerCase() === value.toLowerCase() &&
                         r.eltEmailKey.toLowerCase() !== editing?.eltEmailKey.toLowerCase()
                  );
                  return isDuplicate
                    ? Promise.reject('This email key already exists.')
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input
              prefix={<KeyOutlined style={{ color: '#bbb' }} />}
              placeholder="e.g. ELT1, KEY_01, etc."
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ELTEmail;
